import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties, ComputedRef, Ref } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { Promocode } from './interfaces'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { useSidebarPrivileges } from '@entities/viewer'

import { usePromocodesStore } from './store'

interface UsePromocodeActionsOptions {
  promocode: ComputedRef<Promocode>
}

interface PromocodeActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  canManage: ComputedRef<boolean>
  toggle: () => void
  openEdit: () => void
}

export const usePromocodeActions = ({ promocode }: UsePromocodeActionsOptions): PromocodeActionsReturn => {
  const store = usePromocodesStore()
  const { canManagePromocodes } = useSidebarPrivileges()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const canManage = computed<boolean>(() => canManagePromocodes.value)

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const openEdit = (): void => {
    isOpen.value = false
    store.setPromocodeToEdit(promocode.value)
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    canManage,
    toggle,
    openEdit,
  }
}
